/**
 * KindIcon — per-kind glyph for cards, modal headers, and the Create New
 * picker. Backed by `lucide-react` so the visual language matches the rest
 * of the SPA's icon set (sidebar nav, action icons).
 *
 *   task         → CheckSquare    (sky blue)
 *   ticket       → Ticket         (emerald green)
 *   event        → CalendarDays   (amber)
 *   announcement → Megaphone      (rose)
 *
 * The Phase 2b custom inline-SVG set was redrawn to Lucide for FU-E so
 * Sidebar / Create-New / Card / Card-Detail-Modal / Notifications all
 * reach for the same icons. Color hue per kind is shared via
 * `KIND_COLOR_CLASSES` so the same palette cascades across surfaces.
 */

import { CalendarDays, CheckSquare, Megaphone, Ticket } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { DeliverableKind } from '@/api/types-bridge'
import { i18n } from '@/i18n'

interface KindIconProps {
  kind: DeliverableKind
  /** Pixel size; defaults to 16 to align with the card title baseline. */
  size?: number
  className?: string
}

/**
 * Hue per kind — used by KindIcon, the kind badge on BoardCard, and the
 * column accent stripe. Centralising it here keeps the visual story
 * consistent.
 *
 * FU-E re-tints to David's spec: Task = sky, Ticket = emerald, Event =
 * amber, Announcement = rose. The previous palette mapped Task to
 * emerald (which clashed with the CTA-coloured stop button + the
 * "running timer" emerald pill).
 *
 * `darkText` is the companion to `text`: the `*-700` tints sit at roughly
 * 2.6:1 against the two dark themes' backgrounds, which fails AA. Surfaces
 * that render the glyph on a dark-theme-aware background emit both
 * (`${c.text} ${c.darkText}`); the pill surfaces that pair `text` with
 * their own light `bg` keep using `text` alone.
 *
 * Which is why `bg` carries a `dark:text-*` of its own alongside its dark
 * fill: a `-50` fill is a near-white tile on Obsidian / Midnight, and the
 * `-900/40` fill it becomes there needs the light `-200` ink that the
 * caller's bare `text` does not supply. Every `bg` consumer pairs it with
 * `text` and never with `darkText`, so the two never collide — but a new
 * call site emitting `${c.bg} ${c.darkText}` WOULD set `color` twice.
 * Emit `bg` with `text`, or `text` with `darkText`; never all three.
 */
export const KIND_COLOR_CLASSES: Record<
  DeliverableKind,
  { text: string; darkText: string; bg: string; border: string }
> = {
  task: {
    text: 'text-sky-700',
    darkText: 'dark:text-sky-400',
    bg: 'bg-sky-50 dark:bg-sky-900/40 dark:text-sky-200',
    border: 'border-sky-200 dark:border-sky-700/60',
  },
  ticket: {
    text: 'text-emerald-700',
    darkText: 'dark:text-emerald-400',
    bg: 'bg-emerald-50 dark:bg-emerald-900/40 dark:text-emerald-200',
    border: 'border-emerald-200 dark:border-emerald-700/60',
  },
  event: {
    text: 'text-amber-700',
    darkText: 'dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/40 dark:text-amber-200',
    border: 'border-amber-200 dark:border-amber-700/60',
  },
  announcement: {
    text: 'text-rose-700',
    darkText: 'dark:text-rose-400',
    bg: 'bg-rose-50 dark:bg-rose-900/40 dark:text-rose-200',
    border: 'border-rose-200 dark:border-rose-700/60',
  },
}

/**
 * Kind → `.kind-card` class pair (Claude Design handoff "1b"). The base
 * `kind-card` class reads the `--kind` custom property that its paired
 * `kind-card--<kind>` modifier sets, so a card just spreads BOTH classes —
 * see `.kind-card` in `global.css` for the color-mix rules they drive.
 */
export const KIND_CARD_CLASS: Record<DeliverableKind, string> = {
  task: 'kind-card kind-card--task',
  ticket: 'kind-card kind-card--ticket',
  event: 'kind-card kind-card--event',
  announcement: 'kind-card kind-card--announcement',
}

/**
 * Stable map of kind → catalog key (literal-typed so consumers passing
 * the result to `t()` keep i18next's strict-key inference).
 */
export const KIND_LABEL_KEYS = {
  task: 'kindLabel.task',
  ticket: 'kindLabel.ticket',
  event: 'kindLabel.event',
  announcement: 'kindLabel.announcement',
} as const satisfies Record<DeliverableKind, string>

/**
 * Resolve a localized kind label outside React render (toasts, aria
 * strings built imperatively). Uses the imperative i18next instance so
 * the result reflects the active locale at call time.
 *
 * Inside React render, prefer `useTranslation('board')` and
 * `t(KIND_LABEL_KEYS[kind])` — that reactively re-renders on locale
 * switch.
 */
export function kindLabel(kind: DeliverableKind): string {
  return i18n.t(KIND_LABEL_KEYS[kind], { ns: 'board' })
}

/**
 * Hook variant of `kindLabel` — returns a function that resolves the
 * localized label and re-renders the consumer when the locale changes.
 */
export function useKindLabel(): (kind: DeliverableKind) => string {
  const { t } = useTranslation('board')
  return (kind) => t(KIND_LABEL_KEYS[kind])
}

const KIND_ICON_COMPONENTS: Record<DeliverableKind, typeof CheckSquare> = {
  task: CheckSquare,
  ticket: Ticket,
  event: CalendarDays,
  announcement: Megaphone,
}

export function KindIcon({ kind, size = 16, className = '' }: KindIconProps) {
  const Glyph = KIND_ICON_COMPONENTS[kind]
  return (
    <Glyph
      width={size}
      height={size}
      strokeWidth={1.75}
      aria-hidden="true"
      role="img"
      className={className}
    />
  )
}
